import { useFormik } from "formik";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Filter from "../../../components/functional_components/Filter/Filter";
import { setFiltersList } from "../../../redux/ducks/Filters";
import { paginationDefaults } from "../../../utils/properties";
import { buildQueryString, showError, stringifyObject } from "../../../utils/utilities";
import { Table, List } from "antd";
import Project from "../../../services/api/Project";
import { cloneDeep, get, indexOf, isEmpty } from "lodash";
import { setLoading } from "../../../redux/ducks/Loading";
import { useNavigate } from "react-router-dom";
import { EditOutlined, WarningOutlined } from "@ant-design/icons";
import Vertical from "../../../services/api/Vertical";
import { resetProjectListData, setProjectListData } from "../../../redux/ducks/ProjectManagement";

const ProjectsManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const filtersList = useSelector((state) => state.FiltersDuck.filtersList);
  const projectListData = useSelector((state) => state.ProjectManagementDuck.listProjects);
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      page: filtersList.obj.page ? filtersList.obj.page : paginationDefaults.page - 1,
      size: filtersList.obj.size ? filtersList.obj.size : paginationDefaults.size,
      totalItems: filtersList.obj.totalItems ? filtersList.obj.totalItems : paginationDefaults.totalItems,
      sort: "clientP.name;asc",
    },
    filterLoading: false,
    verticalListFilter: [],
  });

  useEffect(() => {
    const queryString = buildQueryString(filtersList.obj, state.pagination);
    dataList(queryString);
    getVerticalList();
  }, []);

  const getVerticalList = async () => {
    dispatch(setLoading(true));
    const verticalList = await Vertical.getVerticalList("allpages=true");
    let optionVerticalList = [];
    if (!verticalList.error && verticalList.data) {
      verticalList.data.forEach((item) => {
        optionVerticalList.push({ value: item.id, label: item.name });
      });
      setState((prevState) => ({ ...prevState, verticalListFilter: optionVerticalList }));
    } else showError(get(verticalList, "error.messageCode", "ER000"));
    dispatch(setLoading(false));
  };

  const dataList = async (queryString, objAfterReset = {}) => {
    dispatch(setLoading(true));
    const omitValuesToPassBE = ["totalItems"];
    const dataToPass = cloneDeep(queryString.obj);
    Object.keys(dataToPass).forEach((d) => {
      if (indexOf(omitValuesToPassBE, d) !== -1) {
        delete dataToPass[d];
      }
    });
    const clientsList = await Project.getClients(stringifyObject(dataToPass));
    if (!clientsList.error && clientsList.data) {
      clientsList.data.forEach((cc, i) => {
        cc.key = i;
      });
      const pagination = {
        page: clientsList.currentPage ? clientsList.currentPage : paginationDefaults.page - 1,
        size: get(clientsList, "pageSize", paginationDefaults.size),
        totalItems: get(clientsList, "totalItems", paginationDefaults.totalItems),
        sort: "clientP.name;asc",
      };
      const queryStringNew = buildQueryString(isEmpty(objAfterReset) ? filtersList.obj : objAfterReset, pagination);
      dispatch(setFiltersList(queryStringNew));
      setState((prevState) => ({
        ...prevState,
        tableContent: get(clientsList, "data", []),
        pagination,
      }));
    } else showError(get(clientsList, "error.messageCode", "ER000"));
    dispatch(setLoading(false));
  };

  const formikFilter = useFormik({
    initialValues: {
      verticalId: filtersList.obj.verticalId ? filtersList.obj.verticalId : "",
      projectStatusList: filtersList.obj.projectStatusList ? filtersList.obj.projectStatusList : "",
      clientName: "",
      projectId: "",
      projectName: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      let data = cloneDeep(values);
      //data.verticalId;
      //data.projectStatusList;
      //data.clientName;
      //data.projectId;
      //data.projectName;
      data.page = paginationDefaults.page - 1;
      const queryString = buildQueryString(filtersList.obj, data);
      dataList(queryString);
      dispatch(resetProjectListData());
    },
  });

  const resetFilter = () => {
    const queryString = buildQueryString(
      {},
      { page: paginationDefaults.page - 1, size: paginationDefaults.size, sort: "clientP.name;asc" }
    );
    dispatch(setFiltersList(queryString));
    dataList(queryString, queryString.obj);
    dispatch(resetProjectListData());
  };

  const fieldsFilter = [
    {
      type: "select",
      isRequired: false,
      id: "verticalId",
      name: "verticalId",
      value: formikFilter.values.verticalId,
      label: t("projectsManagement.filter.vertical"),
      onChange: formikFilter.handleChange("verticalId"),
      onBlur: formikFilter.handleBlur("verticalId"),
      dataSelect: [{ label: t("projectsManagement.filter.select.all"), value: "" }, ...state.verticalListFilter],
    },
    {
      type: "select",
      isRequired: false,
      id: "projectStatusList",
      name: "projectStatusList",
      value: formikFilter.values.projectStatusList,
      label: t("projectsManagement.filter.status"),
      onChange: formikFilter.handleChange("projectStatusList"),
      onBlur: formikFilter.handleBlur("projectStatusList"),
      dataSelect: [
        { label: t("projectsManagement.filter.select.all"), value: "" },
        { label: t("projectsManagement.filter.select.blocked"), value: "BLOCKED" },
        { label: t("projectsManagement.filter.select.closed"), value: "CLOSED" },
        { label: t("projectsManagement.filter.select.completed"), value: "COMPLETED" },
        { label: t("projectsManagement.filter.select.created"), value: "CREATED" },
        { label: t("projectsManagement.filter.select.planning"), value: "PLANNING" },
        { label: t("projectsManagement.filter.select.released"), value: "RELEASED" },
        { label: t("projectsManagement.filter.select.unknown"), value: "UNKNOWN" },
      ],
    },
    {
      type: "input",
      id: "clientName",
      name: "clientName",
      value: formikFilter.values.clientName,
      label: t("projectsManagement.filter.clientName"),
      onChange: formikFilter.handleChange("clientName"),
      onBlur: formikFilter.handleBlur("clientName"),
      disabled: formikFilter.values.projectId || formikFilter.values.projectName ? true : false,
    },
    {
      type: "input",
      id: "projectId",
      name: "projectId",
      value: formikFilter.values.projectId,
      label: t("projectsManagement.filter.projectId"),
      onChange: formikFilter.handleChange("projectId"),
      onBlur: formikFilter.handleBlur("projectId"),
      disabled: formikFilter.values.clientName || formikFilter.values.projectName ? true : false,
    },
    {
      type: "input",
      id: "projectName",
      name: "projectName",
      value: formikFilter.values.projectName,
      label: t("projectsManagement.filter.projectDescription"),
      onChange: formikFilter.handleChange("projectName"),
      onBlur: formikFilter.handleBlur("projectName"),
      disabled: formikFilter.values.projectId || formikFilter.values.clientName ? true : false,
    },
  ];

  const columns = [
    {
      title: t("projectsManagement.clientName"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: "",
      dataIndex: "alert",
      key: "alert",
      width: 10,
      render: (alert, item) =>
        alert ? <WarningOutlined key={item.id} className="icon-medium PFred noCursorPointer" /> : "",
    },
  ];

  const expandedRowRender = () => {
    return (
      <>
        <List
          header={<strong>{t("projectsManagement.projects")}</strong>}
          bordered
          dataSource={projectListData.expandedTableContent}
          renderItem={(item) => (
            <List.Item
              actions={[
                <>
                  {item.alert && <WarningOutlined className="icon-medium PFred mr-10 noCursorPointer" />}
                  <EditOutlined
                    key={item.id + "-" + Math.random()}
                    onClick={() => navigate(`${item.id}`, { state: { supplierName: item.name } })}
                    className="icon-medium list-actions"
                  />
                </>,
              ]}
            >
              <List.Item.Meta title={<div>{`${item.id} | ${item.name}`}</div>} />
            </List.Item>
          )}
        />
      </>
    );
  };

  const getExpandedRowData = async (expanded, record = state.expandedOrder) => {
    let rowData = [];
    const keys = [];
    if (expanded) {
      dispatch(setLoading(true));
      const queryString = buildQueryString(
        {
          clientPortalId: record.id,
          projectStatusList: formikFilter.values.projectStatusList,
          verticalId: formikFilter.values.verticalId,
          clientName: formikFilter.values.clientName,
          projectId: formikFilter.values.projectId,
          projectName: formikFilter.values.projectName,
        },
        {}
      );
      const projectsManangementlist = await Project.projectsList(queryString.string);
      if (!projectsManangementlist.error) {
        rowData = cloneDeep(projectsManangementlist);
        keys.push(record.key);
      } else showError(get(projectsManangementlist, "error.messageCode", "ER000"));
    }
    if (!isEmpty(rowData.data))
      rowData.data.forEach((projectsManagenent, idx) => {
        projectsManagenent.key = idx;
      });
    dispatch(
      setProjectListData({
        expandedTableContent: rowData.data,
        expandedRowKeys: keys,
      })
    );
    dispatch(setLoading(false));
  };

  return (
    <>
      <Filter
        formikForm={formikFilter}
        fields={fieldsFilter}
        loading={state.filterLoading}
        allInOneRow={false}
        onReset={resetFilter}
        projectManagenent={true}
      />
      <Table
        bordered
        pagination={{
          current: state.pagination.page + 1,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          sort: "clientP.name;asc",
          onChange: (page, pageSize) => {
            const pagination = state.pagination;
            pagination.page = page - 1;
            pagination.size = pageSize;
            const queryString = buildQueryString(filtersList.obj, pagination);
            dispatch(setFiltersList(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination,
            }));
            dataList(queryString);
            dispatch(resetProjectListData());
          },
        }}
        columns={columns}
        expandable={{ expandedRowRender }}
        expandedRowKeys={projectListData.expandedRowKeys}
        dataSource={state.tableContent}
        size="small"
        onExpand={(expanded, record) => getExpandedRowData(expanded, record)}
      />
    </>
  );
};

export default ProjectsManagement;
